import { useState, useEffect } from 'react';
import type { Bird } from '../api/birds';
import { transactionsApi, PaymentMethod } from '../api/transactions';
import toast from 'react-hot-toast';
import {
  BrandModal,
  BrandModalBody,
  BrandModalFooter,
  BrandModalHeader,
} from './ui/BrandModal';
import AmountInput from './ui/AmountInput';

interface SellBirdModalProps {
  isOpen: boolean;
  onClose: () => void;
  bird: Bird;
  onSaleComplete: () => void;
}

// PaymentMethod is now imported from the API

interface SaleFormData {
  buyerName: string;
  buyerPhone: string;
  buyerAddress: string;
  salePrice: string;
  paymentMethod: PaymentMethod;
  saleDate: string;
  notes: string;
}

export default function SellBirdModal({ isOpen, onClose, bird, onSaleComplete }: SellBirdModalProps) {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState<SaleFormData>({
    buyerName: '',
    buyerPhone: '',
    buyerAddress: '',
    salePrice: '',
    paymentMethod: PaymentMethod.CASH,
    saleDate: new Date().toISOString().split('T')[0],
    notes: ''
  });

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setFormData({
        buyerName: '',
        buyerPhone: '',
        buyerAddress: '',
        salePrice: '',
        paymentMethod: PaymentMethod.CASH,
        saleDate: new Date().toISOString().split('T')[0],
        notes: ''
      });
    }
  }, [isOpen]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.buyerName || !formData.salePrice) {
      toast.error('Please fill in all required fields');
      return;
    }

    const salePrice = parseFloat(formData.salePrice);
    if (isNaN(salePrice) || salePrice <= 0) {
      toast.error('Please enter a valid sale price');
      return;
    }

    try {
      setLoading(true);

      // Process bird sale transaction with accounting integration
      const saleTransaction = {
        birdId: bird.id,
        buyerName: formData.buyerName,
        buyerPhone: formData.buyerPhone,
        buyerAddress: formData.buyerAddress,
        salePrice: salePrice,
        paymentMethod: formData.paymentMethod,
        saleDate: new Date(formData.saleDate),
        notes: formData.notes
      };

      const result = await transactionsApi.createBirdSaleTransaction(saleTransaction);
      
      toast.success(`Bird ${bird.bandId} sold successfully! Transaction ID: ${result.id}`);
      onSaleComplete();
      
    } catch (error) {
      console.error('Error processing sale:', error);
      toast.error(`Failed to process sale: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  if (!bird) return null;

  return (
    <BrandModal open={isOpen} onClose={onClose} maxWidthClass="max-w-2xl">
      <BrandModalHeader
        title={`Sell Bird: ${bird.bandId}`}
        subtitle={[
          bird.species?.commonName
            ? `${bird.species.commonName} (${bird.species?.genus} ${bird.species?.species})`
            : undefined,
          bird.mutationName,
        ].filter(Boolean).join(' · ') || undefined}
        onClose={onClose}
      />
      <form onSubmit={handleSubmit}>
        <BrandModalBody className="space-y-6">
                    {/* Buyer Information */}
                    <div className="bg-slate-50/80 p-4 rounded-lg">
                      <h4 className="text-md font-medium text-slate-900 mb-4">Buyer Information</h4>
                      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                        <div>
                          <label htmlFor="buyerName" className="block text-sm font-medium text-slate-700">
                            Buyer Name <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            name="buyerName"
                            id="buyerName"
                            value={formData.buyerName}
                            onChange={handleChange}
                            className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
                            required
                          />
                        </div>

                        <div>
                          <label htmlFor="buyerPhone" className="block text-sm font-medium text-slate-700">
                            Phone Number
                          </label>
                          <input
                            type="tel"
                            name="buyerPhone"
                            id="buyerPhone"
                            value={formData.buyerPhone}
                            onChange={handleChange}
                            className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
                          />
                        </div>

                        <div className="sm:col-span-2">
                          <label htmlFor="buyerAddress" className="block text-sm font-medium text-slate-700">
                            Address
                          </label>
                          <textarea
                            name="buyerAddress"
                            id="buyerAddress"
                            value={formData.buyerAddress}
                            onChange={handleChange}
                            rows={2}
                            className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Sale Information */}
                    <div className="bg-cyan-50 p-4 rounded-lg">
                      <h4 className="text-md font-medium text-slate-900 mb-4">Sale Information</h4>
                      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                        <div>
                          <label htmlFor="salePrice" className="block text-sm font-medium text-slate-700">
                            Sale Price (ZAR) <span className="text-red-500">*</span>
                          </label>
                          <AmountInput
                            id="salePrice"
                            name="salePrice"
                            value={formData.salePrice}
                            onChange={(v) =>
                              handleChange({
                                target: { name: 'salePrice', value: v != null ? String(v) : '' },
                              } as React.ChangeEvent<HTMLInputElement>)
                            }
                            prefix="R"
                            required
                            min={0}
                          />
                        </div>

                        <div>
                          <label htmlFor="paymentMethod" className="block text-sm font-medium text-slate-700">
                            Payment Method <span className="text-red-500">*</span>
                          </label>
                          <select
                            name="paymentMethod"
                            id="paymentMethod"
                            value={formData.paymentMethod}
                            onChange={handleChange}
                            className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
                            required
                          >
                            <option value={PaymentMethod.CASH}>Cash</option>
                            <option value={PaymentMethod.CREDIT_CARD}>Credit Card</option>
                            <option value={PaymentMethod.BANK_TRANSFER}>Bank Transfer</option>
                            <option value={PaymentMethod.MOBILE_PAYMENT}>Mobile Payment</option>
                            <option value={PaymentMethod.CHECK}>Check</option>
                            <option value={PaymentMethod.OTHER}>Other</option>
                          </select>
                        </div>

                        <div>
                          <label htmlFor="saleDate" className="block text-sm font-medium text-slate-700">
                            Sale Date <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="date"
                            name="saleDate"
                            id="saleDate"
                            value={formData.saleDate}
                            onChange={handleChange}
                            className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
                            required
                          />
                        </div>
                      </div>
                    </div>

                    {/* Notes */}
                    <div>
                      <label htmlFor="notes" className="block text-sm font-medium text-slate-700">
                        Notes
                      </label>
                      <textarea
                        name="notes"
                        id="notes"
                        value={formData.notes}
                        onChange={handleChange}
                        rows={3}
                        className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
                        placeholder="Additional notes about the sale..."
                      />
                    </div>
        </BrandModalBody>
        <BrandModalFooter>
          <button
            type="button"
            className="btn btn-secondary text-sm"
            onClick={onClose}
            disabled={loading}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            className="btn btn-primary text-sm"
          >
            {loading ? 'Processing Sale...' : 'Complete Sale'}
          </button>
        </BrandModalFooter>
      </form>
    </BrandModal>
  );
} 